import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import clsx from 'clsx';
import EmptyState from '../components/ui/EmptyState';

// ---- Mock data ----
const mockUserEvents = [
  {
    id: 1,
    title: 'Pottery Making Workshop',
    description: 'Learn the basics of pottery in this hands-on workshop',
    datetime: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000),
    location: 'Creative Arts Studio, CA',
    category: 'Workshop',
    attendees: 8,
    maxAttendees: 12,
    image: '/api/placeholder/300/200',
  },
  {
    id: 2,
    title: 'Abstract Painting Class',
    description: 'Express yourself through abstract art techniques with guided instruction',
    datetime: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
    location: 'Downtown Art Center',
    category: 'Class',
    attendees: 5,
    maxAttendees: 10,
    image: '/api/placeholder/300/200',
  },
  {
    id: 3,
    title: 'Tech Meetup: AI in 2025',
    description: 'Monthly meetup discussing the latest in artificial intelligence',
    datetime: new Date(Date.now() + 10 * 24 * 60 * 60 * 1000),
    location: 'Innovation Hub, SF',
    category: 'Tech',
    attendees: 32,
    maxAttendees: 50,
    image: '/api/placeholder/300/200',
  },
  {
    id: 4,
    title: 'Sunrise Yoga Session',
    description: 'Start your weekend with a rejuvenating outdoor yoga session at the park',
    datetime: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000),
    location: 'Central Park, NY',
    category: 'Wellness',
    attendees: 15,
    maxAttendees: 25,
    image: '/api/placeholder/300/200',
  },
  {
    id: 5,
    title: 'Startup Pitch Night',
    description: 'Watch local startups pitch their ideas to investors and the community',
    datetime: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000),
    location: 'Venture Hall, Austin',
    category: 'Business',
    attendees: 60,
    maxAttendees: 80,
    image: '/api/placeholder/300/200',
  },
];

// ---- Helpers ----
function formatDate(date) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

// ---- Icons ----
const ChevronLeftIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const LocationIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const UsersIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

// ---- Selectable event card ----
function EventSelectCard({ event, isSelected, onToggle, index }) {
  const spotsLeft = event.maxAttendees - event.attendees;
  const isAlmostFull = spotsLeft <= 5;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.28, delay: index * 0.05 }}
      layout
    >
      <button
        type="button"
        onClick={() => onToggle(event.id)}
        className={clsx(
          'w-full text-left bg-white rounded-2xl border transition-all duration-200 overflow-hidden group',
          isSelected
            ? 'border-primary-400 shadow-colored ring-1 ring-primary-400/30'
            : 'border-surface-100 shadow-soft-sm hover:shadow-soft-md hover:border-surface-200'
        )}
      >
        <div className="flex gap-4 p-4">
          {/* Thumbnail */}
          <div className="relative flex-shrink-0 rounded-xl overflow-hidden w-20 h-20">
            <img
              src={event.image}
              alt={event.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
            />
            {/* Category overlay */}
            <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-md">
              {event.category}
            </span>
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <h3 className={clsx(
                'text-body font-semibold line-clamp-2 transition-colors duration-200',
                isSelected ? 'text-primary-700' : 'text-surface-900'
              )}>
                {event.title}
              </h3>
              {/* Checkbox indicator */}
              <div className={clsx(
                'flex-shrink-0 w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all duration-200',
                isSelected
                  ? 'bg-primary-500 border-primary-500'
                  : 'border-surface-300 group-hover:border-primary-400'
              )}>
                {isSelected && (
                  <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-3 gap-y-1">
              <span className="inline-flex items-center gap-1 text-caption text-surface-500">
                <CalendarIcon />
                {formatDate(event.datetime)}
              </span>
              <span className="inline-flex items-center gap-1 text-caption text-surface-500 truncate max-w-[180px]">
                <LocationIcon />
                {event.location}
              </span>
              <span className={clsx(
                'inline-flex items-center gap-1 text-caption font-medium',
                isAlmostFull ? 'text-accent-600' : 'text-surface-500'
              )}>
                <UsersIcon />
                {event.attendees}/{event.maxAttendees}
                {isAlmostFull && ' — Almost full'}
              </span>
            </div>
          </div>
        </div>

        {/* Selected accent bar */}
        <AnimatePresence>
          {isSelected && (
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              exit={{ scaleX: 0 }}
              transition={{ duration: 0.2 }}
              className="h-1 bg-primary-500 origin-left"
            />
          )}
        </AnimatePresence>
      </button>
    </motion.div>
  );
}

// ---- Main component ----
function AddToGroupCliqq() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEventIds, setSelectedEventIds] = useState(new Set());
  const [adding, setAdding] = useState(false);

  const filteredEvents = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return mockUserEvents;
    return mockUserEvents.filter(
      (e) =>
        e.title.toLowerCase().includes(q) ||
        e.category.toLowerCase().includes(q) ||
        e.location.toLowerCase().includes(q) ||
        e.description.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const toggleEvent = (id) => {
    setSelectedEventIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleAdd = async () => {
    if (selectedEventIds.size === 0) {
      toast.warning('Please select at least one event to add.');
      return;
    }
    setAdding(true);
    try {
      // Replace with actual API call:
      // await api.post('/api/groups/:id/events', { event_ids: [...selectedEventIds] });
      await new Promise((r) => setTimeout(r, 700));
      toast.success(
        `${selectedEventIds.size} event${selectedEventIds.size > 1 ? 's' : ''} added to the group!`
      );
      navigate(-1);
    } catch (err) {
      toast.error('Failed to add events to the group. Please try again.');
      setAdding(false);
    }
  };

  const selectedCount = selectedEventIds.size;

  return (
    <div className="min-h-screen bg-surface-50 font-sans flex flex-col">
      {/* Top bar */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-800 px-4 py-4 flex items-center justify-between flex-shrink-0">
        <button
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-xl bg-white/15 text-white hover:bg-white/25 flex items-center justify-center transition-colors duration-200"
          aria-label="Go back"
        >
          <ChevronLeftIcon />
        </button>
        <div className="text-center">
          <span className="text-white text-body font-bold block">Add Events to Group</span>
          {selectedCount > 0 && (
            <motion.span
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-primary-200 text-caption block"
            >
              {selectedCount} selected
            </motion.span>
          )}
        </div>
        <div className="w-9" aria-hidden="true" />
      </div>

      {/* Main content */}
      <div className="flex-1 max-w-2xl mx-auto w-full px-4 py-6">
        {/* Intro */}
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="mb-5"
        >
          <p className="text-body text-surface-500">
            Select events from your list to add them to the group. Members will be able to see and join these events.
          </p>
        </motion.div>

        {/* Search bar */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.05 }}
          className="relative mb-5"
        >
          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-surface-400 pointer-events-none">
            <SearchIcon />
          </div>
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search your events..."
            className="w-full bg-white border border-surface-200 rounded-2xl pl-10 pr-4 py-3 text-body text-surface-900 placeholder-surface-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all duration-200 shadow-soft-sm"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-surface-400 hover:text-surface-600 transition-colors duration-150"
              aria-label="Clear search"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </motion.div>

        {/* Results count + select all */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="flex items-center justify-between mb-4"
        >
          <span className="text-caption text-surface-500 font-medium">
            {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found
          </span>
          {filteredEvents.length > 0 && (
            <button
              type="button"
              onClick={() => {
                const allIds = new Set(filteredEvents.map((e) => e.id));
                const allSelected = filteredEvents.every((e) => selectedEventIds.has(e.id));
                if (allSelected) {
                  setSelectedEventIds((prev) => {
                    const next = new Set(prev);
                    filteredEvents.forEach((e) => next.delete(e.id));
                    return next;
                  });
                } else {
                  setSelectedEventIds((prev) => new Set([...prev, ...allIds]));
                }
              }}
              className="text-caption text-primary-600 font-semibold hover:text-primary-700 transition-colors duration-150"
            >
              {filteredEvents.every((e) => selectedEventIds.has(e.id)) ? 'Deselect all' : 'Select all'}
            </button>
          )}
        </motion.div>

        {/* Event list */}
        <AnimatePresence mode="popLayout">
          {filteredEvents.length > 0 ? (
            <div className="space-y-3 pb-28">
              {filteredEvents.map((event, i) => (
                <EventSelectCard
                  key={event.id}
                  event={event}
                  isSelected={selectedEventIds.has(event.id)}
                  onToggle={toggleEvent}
                  index={i}
                />
              ))}
            </div>
          ) : (
            <motion.div
              key="empty"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <EmptyState
                icon={
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                }
                title={searchQuery ? 'No events match your search' : 'No events found'}
                description={
                  searchQuery
                    ? `No events found for "${searchQuery}". Try different keywords.`
                    : 'You have no events to add to a group yet. Create an event first.'
                }
                actionLabel={searchQuery ? 'Clear search' : 'Create an Event'}
                onAction={searchQuery ? () => setSearchQuery('') : undefined}
                actionTo={!searchQuery ? '/create-event' : undefined}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Sticky footer */}
      <AnimatePresence>
        {(
          <motion.div
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 80, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 400, damping: 35 }}
            className="fixed bottom-0 left-0 right-0 bg-white/90 backdrop-blur-soft border-t border-surface-100 px-4 py-4 shadow-soft-lg"
          >
            <div className="max-w-2xl mx-auto flex items-center gap-3">
              {/* Selected badge */}
              <div className="flex-1 flex items-center gap-2">
                {selectedCount > 0 ? (
                  <motion.span
                    key={selectedCount}
                    initial={{ scale: 0.8, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="inline-flex items-center gap-1.5 bg-primary-50 text-primary-700 px-3 py-1.5 rounded-xl text-body-sm font-semibold"
                  >
                    <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {selectedCount} event{selectedCount > 1 ? 's' : ''} selected
                  </motion.span>
                ) : (
                  <span className="text-body-sm text-surface-400">No events selected</span>
                )}
              </div>

              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-5 py-2.5 bg-surface-100 text-surface-700 rounded-xl text-body-sm font-semibold hover:bg-surface-200 transition-colors duration-200"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleAdd}
                disabled={selectedCount === 0 || adding}
                className={clsx(
                  'flex items-center gap-2 px-6 py-2.5 rounded-xl text-body-sm font-semibold transition-all duration-200',
                  selectedCount > 0 && !adding
                    ? 'bg-primary-500 text-white hover:bg-primary-600 shadow-colored'
                    : 'bg-surface-200 text-surface-400 cursor-not-allowed'
                )}
              >
                {adding && (
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                )}
                {adding ? 'Adding...' : 'Add to Group'}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default AddToGroupCliqq;
